import { BaseExportService } from './BaseExportService';
import { CSVExportService } from './CSVExportService';
import { ExcelExportService } from './ExcelExportService';
import { SimplePDFService } from './SimplePDFService';
import { PrintService } from './PrintService';
import { VaultService } from './VaultService';

interface ExportData {
  data: any[];
  [key: string]: unknown;
}

export class ExportService extends BaseExportService {
  // Export to CSV
  public exportToCSV(exportData: ExportData | any[]): void {
    try {
      console.log("=== EXPORT SERVICE DEBUGGING ===");
      console.log("ExportService.exportToCSV called with data type:", 
        Array.isArray(exportData) ? "array" : "object");
      
      // Check if we have a non-empty data structure
      if (Array.isArray(exportData)) {
        if (exportData.length === 0) {
          console.error("Empty array provided to exportToCSV");
        } else {
          console.log("Array has", exportData.length, "items");
          
          // Check for positions in first item
          const firstItem = exportData[0];
          
          console.log("First item properties:", Object.keys(firstItem));
          
          if (firstItem.positions) {
            console.log("First item has positions:", Object.keys(firstItem.positions));
            
            // Log each position value
            Object.entries(firstItem.positions).forEach(([domain, position]) => {
              console.log(`Domain ${domain} position:`, position);
            });
          } else if (firstItem.competitors) {
            console.log("First item has competitors:", Object.keys(firstItem.competitors));
          } else {
            console.warn("First item has no positions or competitors object");
            console.log("First item data:", JSON.stringify(firstItem));
          }
        }
      } else if (exportData.data) {
        if (exportData.data.length === 0) {
          console.error("Empty data array provided to exportToCSV");
        } else {
          console.log("Data object has", exportData.data.length, "items");
          
          // Check for positions in first item
          const firstItem = exportData.data[0];
          
          console.log("First item properties:", Object.keys(firstItem));
          
          if (firstItem.positions) {
            console.log("First item has positions:", Object.keys(firstItem.positions));
            
            // Log each position value
            Object.entries(firstItem.positions).forEach(([domain, position]) => {
              console.log(`Domain ${domain} position:`, position);
            });
          } else if (firstItem.competitors) {
            console.log("First item has competitors:", Object.keys(firstItem.competitors));
          } else {
            console.warn("First item has no positions or competitors object");
            console.log("First item data:", JSON.stringify(firstItem));
          }
        }
      } else {
        console.error("Invalid data format provided to exportToCSV");
      }
      
      // Pass data directly to CSVExportService without modifying its structure
      // This is critical for position tracking data
      const csvService = new CSVExportService();
      csvService.exportToCSV(exportData);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      throw error;
    }
  }

  // Export to Excel
  public exportToExcel(data: any[], fileName: string): void {
    try {
      const excelService = new ExcelExportService();
      excelService.exportToExcel(data, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw error;
    }
  }

  // Export to PDF
  public exportToPDF(data: any[], fileName: string, title: string): void {
    try {
      const pdfService = new SimplePDFService();
      pdfService.exportToPDF(data, fileName, title);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw error;
    }
  }

  // Print data
  public print(data: any[], title: string): void {
    try {
      const printService = new PrintService();
      printService.print(data, title);
    } catch (error) {
      console.error('Error printing:', error);
      throw error;
    }
  }

  // Save to Resource Vault
  public saveToVault(data: any[], resourceInfo: {
    title: string;
    description: string;
    type: string;
    tags: string[];
    category?: string;
  }): void {
    try {
      const vaultService = new VaultService();
      vaultService.saveToVault(data, {
        ...resourceInfo,
        category: resourceInfo.category || 'InsightRank Audits'
      });
      console.log('Saved to Resource Vault');
    } catch (error) {
      console.error('Error saving to Resource Vault:', error);
      throw error;
    }
  }
}
